/**
 * Turn one of this app's locale codes into a well-formed BCP-47 language tag.
 *
 * The codes in `src/locales/` are all-lowercase (`zh-cn`, `pt-br`, `ta-in`)
 * because they are filenames. BCP-47 is case-insensitive for *matching*, so
 * `Intl` and vue-i18n accept them either way, but the conventional casing is
 * load-bearing in one place that does not do matching: `<html lang>` is read
 * verbatim by assistive technology and by "translate this page", and a tag
 * written in the conventional form is the one every downstream tool is tested
 * against.
 *
 * A FORMATTER, NOT A VALIDATOR. It normalises casing and nothing else, so junk
 * in is junk out (`toBcp47("en-1234")` is `"en-1234"`). That is deliberate:
 * every input comes from a closed set -- `localeLoaders`' 31 codes, or
 * `i18n.global.locale`, which switchLocale only ever sets to one of them -- and
 * a whitelist here would silently blank the tag for a locale added later
 * without touching this file. `tests/unit/documentLangAttribute.test.js` checks
 * every one of the 31 against Intl instead, which is the property that matters.
 *
 * Deliberately dependency-free and free of any vue-i18n import: it is used both
 * by the locale watcher (which owns the i18n instance) and by date formatting
 * (which must stay a pure module), and an import cycle between those two would
 * be a boot-order bug rather than a compile error.
 *
 * @param {string} [locale] - a locale code such as "en-us", "de" or "zh-cn"
 * @returns {string} the BCP-47 tag, or "" when there is no locale to describe.
 *   Empty rather than a guess: `<html lang="">` is "language not stated", which
 *   is honest, whereas `lang="undefined"` is a claim that is simply false.
 */
export function toBcp47(locale) {
  if (!locale || typeof locale !== "string") return "";
  const [language, ...rest] = locale.split("-");
  const subtags = rest.map((subtag) => {
    // Region subtags are two letters (US) or three digits (419); script subtags
    // are four letters (Hant). Everything else is left as written.
    if (/^[A-Za-z]{2}$/.test(subtag)) return subtag.toUpperCase();
    if (/^[A-Za-z]{4}$/.test(subtag)) {
      return subtag[0].toUpperCase() + subtag.slice(1).toLowerCase();
    }
    return subtag.toLowerCase();
  });
  return [language.toLowerCase(), ...subtags].join("-");
}
